import type {
  Collection,
  CollectionIdInput,
  CollectionMembershipType,
  CollectionListFilters,
} from '@/lib/services/collections/collections.types'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import { readProperty } from '@/lib/utils'

import SearchInput from '@/components/form/SearchInput'
import { MediaPlaceholder } from '@/components/patterns/media-placeholder'
import {
  hasSelection,
  toSelectionKey,
  uniqueSelections,
  withoutSelections,
} from '@/components/products/selectionIds'
import { sortSympathyFuneralFirst } from '@/components/products/sortSelectableItems'
import withSuspense from '@/hooks/withSuspense'
import {
  collectionMerchandisingIncludes,
  useCollections,
} from '@/lib/services/collections/collections.query'
import { isSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import { getErrorMessage } from '@/lib/utils'
import { Camera } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox, type CheckboxProps } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import LoadingSpinner from '@/components/ui/loading-spinner'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Typography } from '@/components/ui/typography'
type CheckedState = NonNullable<CheckboxProps['checked']>

// Existing selections may use route/string ids; loaded API rows retain the serializer's number id.
type InitialCollection = Partial<
  Pick<
    Collection,
    'source_system' | 'source_key' | 'occasion_ids' | 'occasions' | 'season_ids' | 'seasons'
  >
> & { id: CollectionIdInput }
type CollectionTagFields = 'occasion_ids' | 'occasions' | 'season_ids' | 'seasons'
type MergedCollection<TInitial> = Omit<Collection, keyof TInitial | CollectionTagFields> &
  Omit<TInitial, CollectionTagFields> &
  Pick<Collection, CollectionTagFields>
interface ProductCollectionPickerProps<TInitial> {
  organizationId?: OrganizationIdInput
  initialData?: TInitial[]
  collectionMembershipType?: CollectionMembershipType | null
  onConfirm?: (collections: (TInitial | Collection | MergedCollection<TInitial>)[]) => void
  onCancel?: () => void
}

function preferLoadedCollectionList<T>(
  initialValue: T[] | undefined,
  loadedValue: T[] | undefined
): T[] | undefined {
  if (Array.isArray(loadedValue)) {
    return loadedValue
  }

  if (Array.isArray(initialValue)) {
    return initialValue
  }

  return loadedValue
}

const ProductCollectionPicker = <TInitial extends InitialCollection>({
  organizationId,
  initialData = [],
  collectionMembershipType = 'manual',
  onConfirm,
  onCancel,
}: ProductCollectionPickerProps<TInitial>) => {
  const [search, setSearch] = useState('')
  const filters: CollectionListFilters = {
    include: collectionMerchandisingIncludes,
  }

  if (collectionMembershipType) {
    filters.collection_membership_type = collectionMembershipType
  }

  const { data: collections = [] } = useCollections({
    organizationId,
    filters,
  })
  const selectableCollections = collections.filter(
    (collection) => !isSubscriptionPackageCollection(collection)
  )
  const [selected, setSelected] = useState(() =>
    initialData
      .filter((collection) => !isSubscriptionPackageCollection(collection))
      .map((collection) => collection.id)
  )

  const normalizedSearch = search.trim().toLowerCase()
  const filteredCollections = sortSympathyFuneralFirst(
    !normalizedSearch
      ? selectableCollections
      : selectableCollections.filter((collection) =>
          collection?.name?.toLowerCase()?.includes(normalizedSearch)
        )
  )
  const filteredCollectionIds = filteredCollections.map((collection) => collection.id)
  const filteredSelectedCount = filteredCollectionIds.filter((id) =>
    hasSelection(selected, id)
  ).length
  const hasFilteredCollections = filteredCollectionIds.length > 0
  const selectAllState =
    hasFilteredCollections && filteredSelectedCount === filteredCollectionIds.length
      ? true
      : filteredSelectedCount > 0
        ? 'indeterminate'
        : false
  const hasAllVisibleSelection =
    hasFilteredCollections && filteredSelectedCount === filteredCollectionIds.length
  const selectAllActionLabel = hasAllVisibleSelection ? 'Select none' : 'Select all'
  const setVisibleSelection = (checked: CheckedState) => {
    if (!hasFilteredCollections) {
      return
    }

    if (checked === true) {
      setSelected((previous) => uniqueSelections([...previous, ...filteredCollectionIds]))
      return
    }

    setSelected((previous) => withoutSelections(previous, filteredCollectionIds))
  }

  return (
    <div className="space-y-4">
      <SearchInput placeholder="Search collections..." value={search} setValue={setSearch} />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Label appearance="choice">
            <Checkbox
              checked={selectAllState}
              disabled={!hasFilteredCollections}
              onCheckedChange={setVisibleSelection}
            />
            <span>{selectAllActionLabel}</span>
          </Label>
          <Typography variant="label" tone="muted" align="right" className="ml-auto">
            {selected.length} collection{selected.length === 1 ? '' : 's'} selected
          </Typography>
        </div>

        <div className="rounded-xl border bg-muted/15">
          <ScrollArea className="h-[min(560px,calc(100dvh-22rem))]">
            {filteredCollections.length > 0 ? (
              <ul className="px-3 py-2">
                {filteredCollections.map((collection, index) => (
                  <li key={collection.id}>
                    <Label htmlFor={`collection-${collection.id}`} appearance="choiceRow">
                      <Checkbox
                        id={`collection-${collection.id}`}
                        checked={hasSelection(selected, collection.id)}
                        onCheckedChange={(isChecked) => {
                          if (isChecked) {
                            setSelected((previous) =>
                              uniqueSelections([...previous, collection.id])
                            )
                          } else {
                            setSelected((previous) => withoutSelections(previous, [collection.id]))
                          }
                        }}
                      />
                      {collection?.media?.[0]?.url ? (
                        <div className="size-14 shrink-0 overflow-hidden rounded-xl border border-border/60 bg-muted/10">
                          <img
                            src={collection.media[0].url}
                            alt={collection.media[0].name || collection.name}
                            className="block h-full w-full object-cover object-center"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <MediaPlaceholder icon={Camera} size="sm" />
                      )}
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{collection.name}</span>
                          {Boolean(
                            readProperty(collection, 'active_collection_price_adjustment') ||
                            collection?.active_price_adjustment
                          ) && <Badge variant="brand">Active price change</Badge>}
                        </div>
                        {collection.slug ? (
                          <Typography variant="label" tone="muted">
                            /collections/{collection.slug}
                          </Typography>
                        ) : null}
                      </div>
                    </Label>
                    {index !== filteredCollections.length - 1 ? <Separator /> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex h-full min-h-[240px] items-center justify-center p-6 text-center">
                <Typography variant="supporting">No collections matched that search.</Typography>
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => onCancel?.()}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            const selectedIds = new Set((selected ?? []).map(toSelectionKey))
            const loadedCollectionsById = new Map(
              (selectableCollections ?? []).map((collection) => [
                toSelectionKey(collection.id),
                collection,
              ])
            )
            const initialCollectionsById = new Map(
              (initialData ?? [])
                .filter((collection) => !isSubscriptionPackageCollection(collection))
                .map((collection) => [toSelectionKey(collection.id), collection])
            )

            onConfirm?.(
              (selected ?? [])
                .filter((collectionId) => selectedIds.has(toSelectionKey(collectionId)))
                .map((collectionId) => {
                  const collectionKey = toSelectionKey(collectionId)
                  const loadedCollection = loadedCollectionsById.get(collectionKey)
                  const initialCollection = initialCollectionsById.get(collectionKey)

                  if (!loadedCollection) {
                    return initialCollection
                  }

                  if (!initialCollection) {
                    return loadedCollection
                  }

                  return {
                    ...loadedCollection,
                    ...initialCollection,
                    occasion_ids: preferLoadedCollectionList(
                      initialCollection.occasion_ids,
                      loadedCollection.occasion_ids
                    ),
                    occasions: preferLoadedCollectionList(
                      initialCollection.occasions,
                      loadedCollection.occasions
                    ),
                    season_ids: preferLoadedCollectionList(
                      initialCollection.season_ids,
                      loadedCollection.season_ids
                    ),
                    seasons: preferLoadedCollectionList(
                      initialCollection.seasons,
                      loadedCollection.seasons
                    ),
                  }
                })
                .filter((collection): collection is NonNullable<typeof collection> =>
                  Boolean(collection)
                )
            )
          }}
        >
          Apply Collections
        </Button>
      </div>
    </div>
  )
}

export default withSuspense(ProductCollectionPicker, {
  error: ({ error }) => <div>Error loading collections: {getErrorMessage(error)}</div>,
  loading: () => (
    <div className="flex items-center">
      <LoadingSpinner className="mr-2" /> Loading collections...
    </div>
  ),
})
